//страница "Пользователи"
import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Switch,
  Alert,
  Avatar,
  Image,
  Tooltip,
  message,
  Table,
  Modal,
  Typography,
} from "antd";
import {
  ClockCircleOutlined,
  CoffeeOutlined,
  StopOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { apiRequests } from "src/shared/api/api";
import { EditUser } from "src/features/Modal";

// import VirtualTable from './VirtualTable';

const formatMinutes = (minutes) => {
  if (minutes == null) return "--:--";
  const h = Math.floor(minutes / 60)
    .toString()
    .padStart(2, "0");
  const m = (minutes % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
};

const statusConfig = {
  on_shift: {
    render: (record) => (
      <Tooltip
        title={`На смене. Всего на смене: ${Math.floor(
          record.shift_duration_minutes / 60
        )} час(-ов) и ${record.shift_duration_minutes % 60} мин.`}
      >
        <Typography style={{ width: "auto", alignItems: "center" }}>
          {formatMinutes(record.shift_duration_minutes)}{" "}
          <ClockCircleOutlined style={{ color: "#1890ff" }} />
        </Typography>
      </Tooltip>
    ),
  },
  on_break: {
    render: (record) => (
      <Tooltip
        title={`На смене (перерыв). Всего на смене: ${Math.floor(
          record.shift_duration_minutes / 60
        )} час(-ов) и ${record.shift_duration_minutes % 60} мин.`}
      >
        <Typography>
          {formatMinutes(record.shift_duration_minutes)}{" "}
          <CoffeeOutlined style={{ fontSize: 18, color: "#fa8c16" }} />
        </Typography>
      </Tooltip>
    ),
  },
  null: {
    render: () => (
      <Tooltip title="Не на смене">
        <Typography.Text style={{ width: "auto", alignItems: "center" }}>
          {"Не на смене "}
          <StopOutlined style={{ color: "red" }} />
        </Typography.Text>
      </Tooltip>
    ),
  },
};

function ShiftStatus({ record }) {
  const status = record.current_shift_status ?? "null";
  const statusRenderer = statusConfig[status];

  return <div>{statusRenderer ? statusRenderer.render(record) : null}</div>;
}

const EditableContext = React.createContext(null);

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  useEffect(() => {
    if (editing) {
      inputRef.current.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({
      [dataIndex]: record[dataIndex],
    });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{
          margin: 0,
        }}
        name={dataIndex}
        rules={[
          {
            required: true,
            message: `${title} обязательно для ввода.`,
          },
        ]}
      >
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{
          paddingRight: 24,
        }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

class UsersTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      count: this.props.c,
      dataSource: this.props.ds,
      currentPage: 1,
      loading: true,
      modalVisible: false,
      selectedUser: null,
      userPermissions: [],
      loadingPermissions: false,
      is_admin: null,
      columns: [],
    };

    this.columns = [];
  }

  handleUserUpdated = () => {
    // Перезапрашиваем данные с сервера после редактирования пользователя
    this.fetch(
      this.state.currentPage,
      `https://${process.env.REACT_APP_APP_URL}/api/v1/cashbox_users/`
    );
  };

  handleChangeStatus = (checked, row) => {
    apiRequests.users
      .put({
        user_id: row.id,
        status: checked,
      })
      .then(() => {
        message.success(<>Вы успешно изменили статус</>);
      });
  };

  componentDidMount() {
    this.fetch(1);
    this.fetchMePermisions();
    const { websocket } = this.props;

    websocket.onmessage = (message) => {
      const updateShiftInList = (shift) => {
        this.setState((prevState) => {
          const newData = prevState.dataSource.map((user) =>
            user.relation_id === shift.user_id
              ? {
                  ...user,
                  current_shift_status:
                    shift.status === "off_shift" ? "null" : shift.status,
                  shift_duration_minutes: Math.floor(
                    (Date.now() - new Date(shift.shift_start + "Z").getTime()) /
                      1000 /
                      60
                  ),
                  // при необходимости добавь и остальные поля смены
                }
              : user
          );
          return { dataSource: newData };
        });
      };

      const employee_shifts_actions = {
        admin_start_shift: updateShiftInList,
        admin_end_shift: updateShiftInList,
        admin_start_break: updateShiftInList,
        admin_end_break: updateShiftInList,
      };

      const data = JSON.parse(message.data);

      if (data.target === "users") {
        if (data.action === "edit") {
          const newData = [...this.state.dataSource];
          const index = newData.findIndex((item) => data.result.id === item.id);

          if (index !== -1) {
            const item = newData[index];
            newData.splice(index, 1, { ...item, ...data.result });
            this.setState({ dataSource: newData });
          }
        }
      }

      if (data.target === "employee_shifts") {
        let action = employee_shifts_actions[data.action];
        if (action) {
          action(data.result);
        }
      }
    };
  }

  fetch = (page) => {
    const limit = 35;
    const offset = page * limit - limit;

    this.setState({ loading: true });

    Promise.all([
      apiRequests.users.getItems({ limit, offset }),
      apiRequests.shifts.getListWithShifts(),
    ])
      .then(([usersResponse, userShiftsResponse]) => {
        // формируем map, где у каждого user_id только одна (последняя) смена
        const userShiftsMap = {};

        userShiftsResponse.data.result.forEach((shift) => {
          userShiftsMap[shift.id] = shift;
        });

        // объединяем пользователей с их актуальными сменами
        const dataSource = usersResponse.data.result.map((user) => ({
          ...user,
          ...(userShiftsMap[user.id] || {}),
        }));

        this.setState({
          count: usersResponse.data.count,
          dataSource,
          loading: false,
        });
      })
      .catch((error) => {
        console.error(error);
        this.setState({ loading: false });
      });
  };

  handleSave = (row) => {
    const newData = [...this.state.dataSource];
    const index = newData.findIndex((item) => row.id === item.id);
    const item = newData[index];

    newData.splice(index, 1, { ...item, ...row });

    this.setState({
      dataSource: newData,
    });

    // edit_request(newData.splice(index, 1, { ...item, ...row })[0]);
  };

  handleRowClick = (record) => {
    if (this.clickedSwitch) {
      this.clickedSwitch = false;
      return;
    }

    if (record.id === 1 && record.is_admin) {
      message.warning(
        "Вы не можете изменить права для основного администратора"
      );
      return;
    }

    this.setState({
      selectedUser: record,
      modalVisible: true,
      loadingPermissions: true,
    });

    this.fetchUserPermissions(record.id);
  };

  handlePermissionsClick = (record) => {
    if (record.id === 1 && record.is_admin) {
      message.warning(
        "Вы не можете изменить права для основного администратора"
      );
      return;
    }

    this.setState({
      selectedUser: record,
      modalVisible: true,
      loadingPermissions: true,
    });

    this.fetchUserPermissions(record.id);
  };

  fetchMePermisions = () => {
    apiRequests.users.getMyPermissions().then(({ data }) => {
      this.setState({ is_admin: data.is_admin });
      this.setState({
        columns: [
          {
            title: "Фото",
            dataIndex: "photo",
            key: "photo",
            width: 80,
            render: (photo_src) => {
              return (
                <Avatar
                  src={
                    <Image
                      src={`https://${process.env.REACT_APP_APP_URL}/${photo_src}`}
                    />
                  }
                  size={60}
                />
              );
            },
          },
          {
            title: "Роль",
            key: "is_admin",
            dataIndex: "is_admin",
            // width: 120,
            width: 20,
            render: (is_owner) => {
              if (is_owner) {
                return <b>Админ</b>;
              } else {
                return <b>Сотрудник</b>;
              }
            },
          },
          {
            title: "Имя",
            key: "first_name",
            dataIndex: "first_name",
            // width: 170,
          },
          {
            title: "Фамилия",
            key: "last_name",
            dataIndex: "last_name",
            // width: 170,
            render: (last_name) => {
              if (last_name) {
                return last_name;
              } else {
                return "Не указана";
              }
            },
          },
          {
            title: "Ссылка ТГ",
            key: "username",
            dataIndex: "username",
            // width: 100,
            width: 200,
            render: (username) => {
              if (username) {
                return (
                  <a
                    target={"_blank"}
                    rel="noreferrer"
                    href={`https://t.me/${username}`}
                  >
                    https://t.me/{username}
                  </a>
                );
              } else {
                return (
                  <Alert
                    message="У этого пользователя нет юзернейма"
                    type="warning"
                  />
                );
              }
            },
          },
          {
            title: "Смена",
            key: "shift",
            width: 150,
            align: "center",
            render: (_, record) => {
              if (!record.shift_work_enabled) {
                return (
                  <Typography.Text color="gray" strong>
                    -
                  </Typography.Text>
                );
              }
              return ShiftStatus({ record });
            },
          },
          data.is_admin === true
            ? {
                title: "Права",
                key: "permissions",
                width: 120,
                render: (_, record) => {
                  if (record.is_admin) {
                    return (
                      <Tooltip title="Администратор имеет все права">
                        <div style={{ display: "flex", gap: "4px" }}>
                          <span
                            style={{
                              background: "#f6ffed",
                              border: "1px solid #b7eb8f",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              fontSize: "12px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Полный доступ
                          </span>
                        </div>
                      </Tooltip>
                    );
                  }
                  return (
                    <Tooltip title="Нажмите для настройки доступа к счетам и платежам">
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            background: "#e6f7ff",
                            border: "1px solid #91d5ff",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            this.handlePermissionsClick(record);
                          }}
                        >
                          Доступ к счетам
                        </span>
                      </div>
                    </Tooltip>
                  );
                },
              }
            : {},
          {
            title: "Статус",
            dataIndex: "status",
            key: "status",
            // width: 50,
            width: 5,
            render: (checked, row) => {
              if (!row.is_admin) {
                return (
                  <Switch
                    checked={checked}
                    onClick={(checked, event) => {
                      event.stopPropagation();
                      this.clickedSwitch = true;
                      this.handleChangeStatus(checked, row);
                    }}
                  />
                );
              }
              return (
                <Tooltip title="Нельзя изменить статус админа!">
                  <WarningOutlined />
                </Tooltip>
              );
            },
          },
          {
            title: "Действия",
            key: "actions",
            dataIndex: "actions",
            width: 100,
            render: (_, record) => {
              return (
                <EditUser
                  users={this.state.dataSource}
                  record={record}
                  onUserUpdated={this.handleUserUpdated}
                />
              );
            },
          },
        ],
      });
    });
  };

  fetchUserPermissions = (userId) => {
    this.setState({ loadingPermissions: true });

    apiRequests.users
      .getPermissions(userId)
      .then((response) => {
        this.setState({
          userPermissions: response.data.permissions || [],
          loadingPermissions: false,
        });
      })
      .catch((error) => {
        console.error("Ошибка загрузки прав пользователя:", error);

        // if (error.response && error.response.status === 404) {
        //     message.warning('Случилась ошибка');

        //     const demoPermissions = [
        //         { section: 'payments', can_view: true, can_edit: false, paybox_id: null, paybox_name: null },
        //         { section: 'pboxes', can_view: true, can_edit: false, paybox_id: null, paybox_name: null },
        //         { section: 'payments', can_view: true, can_edit: false, paybox_id: 1, paybox_name: 'Основной счёт' },
        //         { section: 'pboxes', can_view: true, can_edit: false, paybox_id: 1, paybox_name: 'Основной счёт' }
        //     ];

        //     this.setState({
        //         userPermissions: demoPermissions,
        //         loadingPermissions: false
        //     });
        // } else {
        //     message.error('Не удалось загрузить права пользователя');
        //     this.setState({
        //         userPermissions: [],
        //         loadingPermissions: false
        //     });
        // }
      });
  };

  handleModalCancel = () => {
    this.setState({
      modalVisible: false,
      selectedUser: null,
    });
  };

  saveUserPermissions = (permissions) => {
    const { selectedUser } = this.state;

    const filteredPermissions = permissions.filter(
      (perm) => perm.section === "payments" || perm.section === "pboxes"
    );

    apiRequests.users
      .setPermissions(selectedUser.id, filteredPermissions)
      .then(() => {
        message.success("Права пользователя успешно обновлены");
        this.setState({
          modalVisible: false,
          selectedUser: null,
        });
      })
      .catch((error) => {
        console.error("Ошибка сохранения прав пользователя:", error);

        if (error.response && error.response.status === 404) {
          message.warning(
            "Ошибка при сохранении прав. Изменения не сохранены."
          );

          this.setState({
            modalVisible: false,
            selectedUser: null,
          });
        } else {
          message.error("Не удалось сохранить права пользователя");
        }
      });
  };

  render() {
    const {
      dataSource,
      modalVisible,
      selectedUser,
      userPermissions,
      loadingPermissions,
    } = this.state;
    const components = {
      body: {
        row: EditableRow,
        cell: EditableCell,
      },
    };
    const columns = this.state.columns.map((col) => {
      if (!col.editable) {
        return col;
      }

      return {
        ...col,
        onCell: (record) => ({
          record,
          editable: col.editable,
          dataIndex: col.dataIndex,
          title: col.title,
          handleSave: this.handleSave,
        }),
      };
    });

    return (
      <div>
        {this.props.invite ? (
          <div style={{ marginBottom: 10 }}>
            <b>Ссылка для приглашения:</b>{" "}
            <a
              rel="noreferrer"
              target={"_blank"}
              className="button"
              href={`https://t.me/tablecrmbot?start=${this.props.invite}`}
            >{`https://t.me/tablecrmbot?start=${this.props.invite}`}</a>
          </div>
        ) : null}

        <div style={{ marginBottom: 15 }}>
          <Alert
            message="Нажмите на пользователя для настройки доступа к счетам и платежам"
            type="info"
            showIcon
          />
        </div>

        <Table
          components={components}
          rowClassName={(record) => record.is_deleted && "disabled-row"}
          rowKey={(record) => record.id}
          bordered
          // scroll={{
          //     y: 600,
          //     x: '85vw',
          // }}
          loading={this.state.loading}
          dataSource={dataSource}
          columns={columns}
          pagination={{
            total: this.state.count,
            pageSize: 100,
          }}
          onRow={() => ({
            onClick: () => {
              // Здесь можно реализовать открытие модалки редактирования пользователя, если нужно
              // Например: this.handleEditUser(record);
            },
          })}
        />

        {selectedUser && (
          <UserPermissionsModal
            visible={modalVisible}
            user={selectedUser}
            permissions={userPermissions}
            loading={loadingPermissions}
            onCancel={this.handleModalCancel}
            onSave={this.saveUserPermissions}
          />
        )}
      </div>
    );
  }
}

const UserPermissionsModal = ({
  visible,
  user,
  permissions,
  loading,
  onCancel,
  onSave,
}) => {
  const [sections] = useState(["payments", "pboxes"]);
  const [editedPermissions, setEditedPermissions] = useState([]);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  useEffect(() => {
    setEditedPermissions(permissions || []);

    setLoadingAccounts(true);
    apiRequests.payboxes
      .getItems()
      .then((response) => {
        setAvailableAccounts(response.data.result || []);
        setLoadingAccounts(false);
      })
      .catch(() => {
        setAvailableAccounts([]);
        setLoadingAccounts(false);
      });
  }, [permissions]);

  const getSectionPermission = (sectionName) => {
    return (
      editedPermissions.find(
        (p) => p.section === sectionName && p.paybox_id === null
      ) || {
        section: sectionName,
        can_view: false,
        can_edit: false,
      }
    );
  };

  // const getPayboxPermission = (sectionName, payboxId) => {
  //     return editedPermissions.find(
  //         p => p.section === sectionName && p.paybox_id === payboxId
  //     );
  // };

  const handleSectionPermissionChange = (sectionName, type, checked) => {
    const newPermissions = [...editedPermissions];
    let permIndex = newPermissions.findIndex(
      (p) => p.section === sectionName && p.paybox_id === null
    );

    if (permIndex >= 0) {
      newPermissions[permIndex] = {
        ...newPermissions[permIndex],
        [type]: checked,
      };

      if (type === "can_view" && !checked) {
        newPermissions[permIndex].can_edit = false;
      }
    } else if (checked) {
      newPermissions.push({
        section: sectionName,
        can_view: type === "can_view",
        can_edit: type === "can_edit",
        paybox_id: null,
        paybox_name: null,
      });
    }

    setEditedPermissions(newPermissions);
  };

  const handlePayboxPermissionChange = (
    sectionName,
    payboxId,
    payboxName,
    checked
  ) => {
    const newPermissions = [...editedPermissions];
    let permIndex = newPermissions.findIndex(
      (p) => p.section === sectionName && p.paybox_id === payboxId
    );

    if (permIndex >= 0) {
      newPermissions[permIndex] = {
        ...newPermissions[permIndex],
        can_view: checked,
      };

      if (!checked) {
        newPermissions[permIndex].can_edit = false;
      }
    } else if (checked) {
      newPermissions.push({
        section: sectionName,
        can_view: true,
        can_edit: false,
        paybox_id: payboxId,
        paybox_name: payboxName,
      });
    }

    setEditedPermissions(newPermissions);
  };

  const hasPayboxPermission = (sectionName, payboxId) => {
    return editedPermissions.some(
      (p) =>
        p.section === sectionName &&
        p.paybox_id === payboxId &&
        p.can_view === true
    );
  };

  const handleSave = () => {
    onSave(editedPermissions);
  };

  return (
    <Modal
      title={`Права пользователя: ${user.first_name} ${user.last_name || ""}`}
      open={visible}
      onCancel={onCancel}
      onOk={handleSave}
      width={800}
      confirmLoading={loading}
    >
      <div
        className="permissions-container"
        style={{
          maxHeight: "60vh",
          overflow: "auto",
          padding: "0 20px",
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
        }}
      >
        <h3
          style={{
            marginBottom: "16px",
            marginTop: "16px",
            fontSize: "18px",
            borderBottom: "1px solid #e8e8e8",
            paddingBottom: "8px",
          }}
        >
          Права доступа
        </h3>

        {sections.map((section) => {
          const sectionPermission = getSectionPermission(section);
          return (
            <div
              key={section}
              style={{
                marginBottom: "15px",
                padding: "12px",
                borderRadius: "8px",
                backgroundColor: "#fff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <h4
                  style={{
                    margin: 0,
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#333",
                  }}
                >
                  {translateSection(section)}
                </h4>

                <div style={{ display: "flex", gap: "12px" }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      background: sectionPermission.can_view
                        ? "#f0f7ff"
                        : "transparent",
                      padding: "4px 8px",
                      borderRadius: "4px",
                    }}
                  >
                    <Switch
                      size="small"
                      checked={sectionPermission.can_view}
                      onChange={(checked) =>
                        handleSectionPermissionChange(
                          section,
                          "can_view",
                          checked
                        )
                      }
                      style={{ marginRight: "8px" }}
                    />
                    Просмотр
                  </label>

                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: sectionPermission.can_view
                        ? "pointer"
                        : "not-allowed",
                      opacity: sectionPermission.can_view ? 1 : 0.5,
                      background: sectionPermission.can_edit
                        ? "#e6f7ff"
                        : "transparent",
                      padding: "4px 8px",
                      borderRadius: "4px",
                    }}
                  >
                    <Switch
                      size="small"
                      disabled={!sectionPermission.can_view}
                      checked={sectionPermission.can_edit}
                      onChange={(checked) =>
                        handleSectionPermissionChange(
                          section,
                          "can_edit",
                          checked
                        )
                      }
                      style={{ marginRight: "8px" }}
                    />
                    Редактирование
                  </label>
                </div>
              </div>

              {/* Для разделов "Платежи" и "Счета" показываем доступ к конкретным счетам */}
              <div
                style={{
                  marginTop: "10px",
                  padding: "10px",
                  background: "#f9f9f9",
                  borderRadius: "6px",
                  borderLeft: "4px solid #1890ff",
                }}
              >
                <h5
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "14px",
                    color: "#666",
                  }}
                >
                  {section === "payments"
                    ? "Доступные счета для платежей:"
                    : "Доступные счета для просмотра:"}
                </h5>
                {loadingAccounts ? (
                  <div>Загрузка счетов...</div>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(180px, 1fr))",
                      gap: "8px",
                    }}
                  >
                    {availableAccounts.map((account) => (
                      <label
                        key={account.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          cursor: sectionPermission.can_view
                            ? "pointer"
                            : "not-allowed",
                          opacity: sectionPermission.can_view ? 1 : 0.5,
                          padding: "4px 8px",
                          borderRadius: "4px",
                          background: hasPayboxPermission(section, account.id)
                            ? "#e6f7ff"
                            : "transparent",
                          border: hasPayboxPermission(section, account.id)
                            ? "1px solid #91d5ff"
                            : "1px solid transparent",
                        }}
                      >
                        <Switch
                          size="small"
                          disabled={!sectionPermission.can_view}
                          checked={hasPayboxPermission(section, account.id)}
                          onChange={(checked) =>
                            handlePayboxPermissionChange(
                              section,
                              account.id,
                              account.name,
                              checked
                            )
                          }
                          style={{ marginRight: "8px" }}
                        />
                        <span
                          style={{
                            fontSize: "14px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {account.name}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

function translateSection(section) {
  const translations = {
    payments: "Платежи",
    pboxes: "Счета",
  };

  return translations[section] || section;
}

export default UsersTable;
